import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import ExpandMoreIcon from "@material-ui/icons/ExpandMoreRounded";
import SearchIcon from "@material-ui/icons/SearchRounded";
import { format, isToday, isYesterday, differenceInMilliseconds } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { connectToSocket } from "../../services/socket";

// Execuções em PILL + POPOVER (substitui a antiga coluna ExecutionsPanel).
// Fica sobreposto no canto superior-esquerdo do canvas. O pill mostra a última
// execução (ou a em foco) + contagem do dia, em tempo real; o popover traz a lista
// com busca, filtros e navegação por teclado. Foco = qual execução está sendo
// inspecionada (dirige a aba "Teste & resultados"); o desenho do caminho no canvas
// é escopo à parte (fase futura).
//
// Tokens que o tema (theme.app) ainda não tem, do design system do handoff:
const SURFACE_CARD = "#F3F4F6"; // linha selecionada
const HAIRLINE = "#D1D5DB"; // bordas
const ERROR_DEEP = "#991B1B"; // texto de erro

const useStyles = makeStyles(theme => ({
  root: { position: "absolute", top: 16, left: 16, zIndex: 6, fontFamily: theme.typography.fontFamily },

  // --- PILL ---
  pill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 44,
    padding: "0 14px",
    borderRadius: 999,
    background: "#fff",
    border: `1px solid ${HAIRLINE}`,
    boxShadow: "0 1px 2px rgba(56,66,79,0.06)",
    cursor: "pointer",
    font: "inherit",
    color: theme.app.ink,
    whiteSpace: "nowrap",
    maxWidth: 460,
    "&:hover": { background: theme.app.hover },
    "&:disabled": { cursor: "default", color: theme.app.textTertiary, "&:hover": { background: "#fff" } },
  },
  pillOpen: { borderColor: theme.app.ink },
  dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  dotOk: { background: theme.app.ok },
  dotErr: { background: theme.app.danger },
  dotInfo: { background: theme.app.textTertiary },
  pillTime: { fontFamily: theme.app.mono, fontSize: 13, fontWeight: 500 },
  sep: { width: 1, height: 16, background: HAIRLINE, flexShrink: 0 },
  counts: { fontSize: 12.5, color: theme.app.textSecondary },
  err: { color: ERROR_DEEP, fontWeight: 500 },
  chev: { fontSize: 18, color: theme.app.textTertiary, transition: "transform .15s", marginLeft: 2 },
  chevUp: { transform: "rotate(180deg)" },
  empty: { fontSize: 13, color: theme.app.textTertiary },

  // --- POPOVER ---
  popover: {
    position: "absolute",
    top: 52,
    left: 0,
    width: 400,
    maxHeight: 620,
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    border: `1px solid ${theme.app.ink}`,
    borderRadius: 16,
    boxShadow: "0 12px 32px rgba(56,66,79,0.14)", // exceção de sombra p/ popover (handoff)
    overflow: "hidden",
  },
  head: { padding: "12px 12px 10px", borderBottom: `1px solid ${theme.app.border}`, flexShrink: 0 },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 40,
    padding: "0 12px",
    borderRadius: 999,
    background: SURFACE_CARD,
    "& svg": { fontSize: 18, color: theme.app.textTertiary },
  },
  searchInput: { flex: 1, border: "none", outline: "none", background: "transparent", font: "inherit", fontSize: 13, color: theme.app.ink },
  filters: { display: "flex", alignItems: "center", gap: 7, marginTop: 10 },
  fchip: {
    fontSize: 12,
    padding: "5px 11px",
    borderRadius: 999,
    border: `1px solid ${theme.app.border}`,
    background: "#fff",
    color: theme.app.textSecondary,
    cursor: "pointer",
    font: "inherit",
    "&:hover": { background: theme.app.hover },
  },
  fchipOn: { background: theme.app.dark, color: "#fff", borderColor: theme.app.dark, fontWeight: 500, "&:hover": { background: theme.app.dark } },
  period: { marginLeft: "auto", fontSize: 12, color: theme.app.textSecondary, border: `1px solid ${theme.app.border}`, borderRadius: 8, padding: "4px 6px", background: "#fff", font: "inherit", cursor: "pointer" },

  list: { flex: 1, overflowY: "auto", padding: "4px 8px 8px", ...theme.scrollbarStyles },
  daylbl: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", color: theme.app.textTertiary, fontWeight: 600, padding: "12px 6px 6px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 10px",
    borderRadius: 10,
    cursor: "pointer",
    "&:hover": { background: theme.app.hover },
  },
  rowSel: { background: SURFACE_CARD },
  rowHi: { boxShadow: `inset 0 0 0 1px ${theme.app.ink}` },
  hora: { fontFamily: theme.app.mono, fontSize: 13, fontWeight: 400, color: theme.app.ink, flexShrink: 0 },
  resumo: { flex: 1, minWidth: 0, fontSize: 12.5, color: theme.app.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dur: { fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textTertiary, flexShrink: 0 },
  emptyList: { padding: "26px 18px", textAlign: "center", color: theme.app.textTertiary, fontSize: 13, lineHeight: 1.5 },

  foot: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", borderTop: `1px solid ${theme.app.border}`, flexShrink: 0, fontSize: 11.5, color: theme.app.textTertiary },
  kbd: { fontFamily: theme.app.mono },
  seeAll: { color: theme.app.info, cursor: "pointer", font: "inherit", background: "none", border: "none", padding: 0, "&:hover": { textDecoration: "underline" } },
}));

const dayKey = d => {
  const date = new Date(d);
  if (isToday(date)) return "Hoje";
  if (isYesterday(date)) return "Ontem";
  return format(date, "dd/MM");
};

const fmtDur = r => {
  const ms = Math.max(0, differenceInMilliseconds(new Date(r.endedAt), new Date(r.startedAt)));
  return ms < 10000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
};

const ExecutionsPill = ({ appId, selectedRunId, onSelect, onExit }) => {
  const classes = useStyles();
  const history = useHistory();
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | error | success
  // padrão "Tudo": mostra as execuções mais recentes independentemente do dia (o
  // painel antigo mostrava todas). "Hoje" escondia a lista quando o app não rodou
  // hoje — mesmo havendo execuções recentes de dias anteriores. Hoje/7d ficam como opção.
  const [period, setPeriod] = useState("all"); // all | 7d | today
  const [hi, setHi] = useState(-1); // índice destacado na lista visível (teclado)
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    if (!appId) return;
    try {
      const { data } = await api.get("/events/runs", { params: { applicationId: appId } });
      setRuns(data.runs || []);
    } catch (err) {
      toastError(err);
    }
  }, [appId]);

  useEffect(() => {
    load();
    if (!appId) return undefined;
    const socket = connectToSocket();
    socket.emit("joinRoom", "events");
    socket.on("event", p => {
      if (p.action === "create" && String(p.event.applicationId) === String(appId)) load();
    });
    return () => {
      socket.emit("leaveRoom", "events");
      socket.disconnect();
    };
  }, [appId, load]);

  const today = useMemo(() => runs.filter(r => isToday(new Date(r.startedAt))), [runs]);
  const todayErr = useMemo(() => today.filter(r => r.status === "error").length, [today]);

  // recorte por período (padrão Hoje)
  const periodRuns = useMemo(() => {
    if (period === "all") return runs;
    if (period === "today") return runs.filter(r => isToday(new Date(r.startedAt)));
    const cut = Date.now() - 7 * 24 * 3600 * 1000; // 7 dias
    return runs.filter(r => new Date(r.startedAt).getTime() >= cut);
  }, [runs, period]);

  const counts = useMemo(
    () => ({
      all: periodRuns.length,
      error: periodRuns.filter(r => r.status === "error").length,
      success: periodRuns.filter(r => r.status === "success").length,
    }),
    [periodRuns]
  );

  // filtro de status + busca (client-side: horário e resumo). Telefone/body ficam
  // p/ quando o backend indexar (LGPD) — hoje não vêm no resumo da run.
  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return periodRuns.filter(r => {
      if (filter === "error" && r.status !== "error") return false;
      if (filter === "success" && r.status !== "success") return false;
      if (!term) return true;
      const hora = format(new Date(r.startedAt), "dd/MM/yyyy HH:mm:ss");
      return hora.toLowerCase().includes(term) || String(r.label || "").toLowerCase().includes(term);
    });
  }, [periodRuns, filter, q]);

  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    visible.forEach(r => {
      const k = dayKey(r.startedAt);
      if (!cur || cur.key !== k) {
        cur = { key: k, runs: [] };
        out.push(cur);
      }
      cur.runs.push(r);
    });
    return out;
  }, [visible]);

  const focused = useMemo(() => runs.find(r => r.runId === selectedRunId) || null, [runs, selectedRunId]);
  const pillRun = focused || runs[0] || null;

  const dotFor = s => (s === "error" ? classes.dotErr : s === "success" ? classes.dotOk : classes.dotInfo);

  // navegação por teclado entre execuções (com ou sem popover aberto)
  const move = useCallback(
    delta => {
      if (!visible.length) return;
      const curIdx = visible.findIndex(r => r.runId === selectedRunId);
      const base = curIdx < 0 ? (delta > 0 ? -1 : 0) : curIdx;
      const ni = Math.min(visible.length - 1, Math.max(0, base + delta));
      setHi(ni);
      onSelect(visible[ni]);
    },
    [visible, selectedRunId, onSelect]
  );

  useEffect(() => {
    const onKey = e => {
      const t = e.target || {};
      const tag = (t.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || t.isContentEditable;
      if (e.key === "Escape") {
        if (open) setOpen(false);
        else if (selectedRunId && onExit) onExit();
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "e" || e.key === "E") {
        if (runs.length) setOpen(o => !o);
        e.preventDefault();
      } else if (e.key === "ArrowDown") {
        move(1);
        e.preventDefault();
      } else if (e.key === "ArrowUp") {
        move(-1);
        e.preventDefault();
      } else if (e.key === "Enter" && open && hi >= 0 && visible[hi]) {
        onSelect(visible[hi]);
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, hi, visible, runs.length, selectedRunId, move, onSelect, onExit]);

  // fecha ao clicar fora
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // mostra a DATA quando a execução não é de hoje — senão "0 hoje · Última 11:07"
  // parece que houve execução hoje. Horário sempre em fuso local.
  const fmtWhen = d => {
    const dt = new Date(d);
    return isToday(dt) ? format(dt, "HH:mm:ss") : format(dt, "dd/MM HH:mm");
  };
  const pillTime = pillRun ? (focused ? fmtWhen(pillRun.startedAt) : `Última ${fmtWhen(pillRun.startedAt)}`) : "";

  return (
    <div className={classes.root} ref={rootRef}>
      <button
        type="button"
        className={clsx(classes.pill, open && classes.pillOpen)}
        onClick={() => runs.length && setOpen(o => !o)}
        disabled={!pillRun}
        aria-expanded={open}
        aria-controls="exec-popover"
      >
        {!pillRun ? (
          <span className={classes.empty}>Nenhuma execução ainda</span>
        ) : (
          <>
            <span className={clsx(classes.dot, dotFor(pillRun.status))} />
            <span className={classes.pillTime}>{pillTime}</span>
            <span className={classes.sep} />
            <span className={classes.counts}>
              {today.length} hoje
              {todayErr > 0 && (
                <>
                  {" · "}
                  <span className={classes.err}>{todayErr} {todayErr === 1 ? "erro" : "erros"}</span>
                </>
              )}
            </span>
            <ExpandMoreIcon className={clsx(classes.chev, open && classes.chevUp)} />
          </>
        )}
      </button>

      {open && pillRun && (
        <div className={classes.popover} id="exec-popover" role="dialog" aria-label="Execuções">
          <div className={classes.head}>
            <div className={classes.searchBox}>
              <SearchIcon />
              <input
                className={classes.searchInput}
                placeholder="Buscar por horário ou resumo…"
                value={q}
                onChange={e => setQ(e.target.value)}
                autoFocus
              />
            </div>
            <div className={classes.filters}>
              <button type="button" className={clsx(classes.fchip, filter === "all" && classes.fchipOn)} onClick={() => setFilter("all")}>
                Todas {counts.all}
              </button>
              <button type="button" className={clsx(classes.fchip, filter === "error" && classes.fchipOn)} onClick={() => setFilter("error")}>
                Erros {counts.error}
              </button>
              <button type="button" className={clsx(classes.fchip, filter === "success" && classes.fchipOn)} onClick={() => setFilter("success")}>
                Sucesso {counts.success}
              </button>
              <select className={classes.period} value={period} onChange={e => setPeriod(e.target.value)} title="Período">
                <option value="all">Tudo</option>
                <option value="7d">7 dias</option>
                <option value="today">Hoje</option>
              </select>
            </div>
          </div>

          <div className={classes.list}>
            {groups.length === 0 ? (
              <div className={classes.emptyList}>
                {q.trim() ? "Nada encontrado para essa busca." : filter === "error" ? "Nenhuma falha no período. 🎉" : "Nenhuma execução no período."}
              </div>
            ) : (
              groups.map(g => (
                <React.Fragment key={g.key}>
                  <div className={classes.daylbl}>{g.key}</div>
                  {g.runs.map(r => {
                    const idx = visible.indexOf(r);
                    return (
                      <div
                        key={r.runId}
                        className={clsx(classes.row, selectedRunId === r.runId && classes.rowSel, hi === idx && classes.rowHi)}
                        onClick={() => { setHi(idx); onSelect(r); setOpen(false); }}
                        title={r.label}
                      >
                        <span className={clsx(classes.dot, dotFor(r.status))} />
                        <span className={classes.hora}>{format(new Date(r.startedAt), "HH:mm:ss")}</span>
                        <span className={classes.resumo}>{r.label}</span>
                        <span className={classes.dur}>{fmtDur(r)}</span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </div>

          <div className={classes.foot}>
            <span>
              <span className={classes.kbd}>E</span> abre · <span className={classes.kbd}>↑↓</span> navega · <span className={classes.kbd}>Esc</span> fecha
            </span>
            <button type="button" className={classes.seeAll} onClick={() => history.push("/events")}>
              Ver todas
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionsPill;
